/**
 * Settings > Automations. Each rule the desk can run (reminders, SLA warnings,
 * auto-close, and so on) with an on/off switch, its settings as JSON, and a
 * log of recent runs. The behavior lives in code; only the switch and the
 * settings live here.
 */

import { useState } from 'react';
import { formatInstant } from '@ampersandbrands/ui';
import { useAutomationRuns, useAutomations, usePatchAutomation } from '@/lib/useServiceDesk';
import type { SdAutomation } from '@/lib/serviceDeskTypes';
import { SdError } from './SdShell';
import { SD_BTN_GHOST, SD_BTN_PRIMARY, SD_CARD, SD_INPUT, SD_MUTED } from './sdStyles';

function RunLog({ id }: { id: number }) {
  const runs = useAutomationRuns(id, true);
  if (runs.isLoading) return <p className={`text-[13px] ${SD_MUTED}`}>Loading runs...</p>;
  const list = runs.data ?? [];
  if (list.length === 0) return <p className={`text-[13px] ${SD_MUTED}`}>This rule has not run yet.</p>;
  return (
    <table className="mt-1 min-w-full text-[12px]">
      <thead>
        <tr className={`text-left ${SD_MUTED}`}>
          <th className="py-1 pr-3">When</th>
          <th className="py-1 pr-3">Result</th>
          <th className="py-1 pr-3">Ticket</th>
          <th className="py-1">Detail</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-amp-line dark:divide-amp-dark-line">
        {list.map((r) => (
          <tr key={r.id} className="align-top text-amp-ink dark:text-amp-dark-ink">
            <td className="whitespace-nowrap py-1 pr-3">{formatInstant(r.started_at)}</td>
            <td className="py-1 pr-3">{r.outcome ?? '--'}</td>
            <td className="py-1 pr-3">{r.ticket_id ?? '--'}</td>
            <td className="py-1">{r.detail ?? ''}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function RuleCard({ rule }: { rule: SdAutomation }) {
  const patch = usePatchAutomation();
  const [open, setOpen] = useState(false);
  const [json, setJson] = useState(JSON.stringify(rule.config ?? {}, null, 2));
  const [jsonError, setJsonError] = useState<string | null>(null);

  const save = () => {
    try {
      const parsed: unknown = JSON.parse(json);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) throw new Error('Settings must be a JSON object.');
      setJsonError(null);
      patch.mutate({ id: rule.id, patch: { config: parsed as Record<string, unknown> } });
    } catch (e) {
      setJsonError(e instanceof Error ? e.message : 'That is not valid JSON.');
    }
  };

  return (
    <li className={SD_CARD}>
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-amp-ink dark:text-amp-dark-ink">{rule.name}</p>
          {rule.description && <p className={`text-[13px] ${SD_MUTED}`}>{rule.description}</p>}
          <p className={`mt-1 text-[12px] ${SD_MUTED}`}>
            {rule.last_run_at ? `Last ran ${formatInstant(rule.last_run_at)}` : 'Has not run yet'}
            {rule.last_run_summary ? `: ${rule.last_run_summary}` : ''}
          </p>
        </div>
        <label className="flex cursor-pointer items-center gap-2 text-[13px] text-amp-ink dark:text-amp-dark-ink">
          <input
            type="checkbox"
            role="switch"
            checked={rule.enabled}
            disabled={patch.isPending}
            onChange={(e) => patch.mutate({ id: rule.id, patch: { enabled: e.target.checked } })}
            aria-label={`${rule.name} on or off`}
          />
          {rule.enabled ? 'On' : 'Off'}
        </label>
        <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} className={SD_BTN_GHOST}>
          {open ? 'Hide details' : 'Settings and run log'}
        </button>
      </div>
      {open && (
        <div className="mt-4 grid gap-5 lg:grid-cols-2">
          <div>
            <label htmlFor={`sd-rule-${rule.id}`} className={`mb-1 block text-[12px] font-semibold uppercase tracking-caps-pill ${SD_MUTED}`}>
              Settings (JSON)
            </label>
            <textarea
              id={`sd-rule-${rule.id}`}
              rows={8}
              spellCheck={false}
              value={json}
              onChange={(e) => setJson(e.target.value)}
              className={`${SD_INPUT} font-mono`}
            />
            <div className="mt-2 flex items-center gap-2">
              <button type="button" onClick={save} disabled={patch.isPending} className={SD_BTN_PRIMARY}>
                Save settings
              </button>
              {jsonError && <span className="text-[13px] text-red-700 dark:text-red-300">{jsonError}</span>}
            </div>
          </div>
          <div>
            <p className={`mb-1 text-[12px] font-semibold uppercase tracking-caps-pill ${SD_MUTED}`}>Recent runs</p>
            <RunLog id={rule.id} />
          </div>
        </div>
      )}
      <SdError error={patch.error} what="update the rule" />
    </li>
  );
}

export function AutomationsTab() {
  const q = useAutomations();
  if (q.isLoading) return <p className={`text-sm ${SD_MUTED}`}>Loading automations...</p>;
  const rules = q.data ?? [];
  return (
    <div>
      <SdError error={q.error} what="load the automations" />
      {rules.length === 0 ? (
        <p className={`text-sm ${SD_MUTED}`}>No automations are set up for this desk.</p>
      ) : (
        <ul className="flex flex-col gap-3">
          {rules.map((r) => (
            <RuleCard key={r.id} rule={r} />
          ))}
        </ul>
      )}
    </div>
  );
}
